import { ReactNode } from 'react';

import { styled } from '@mui/material';
import round from 'lodash/round';

import { Icons, mergeSx, Stack, tokens } from '@cast/design-system';
import type { ComputeResourceType } from '@cast/shared-types';
import { WorkloadResourceConfig } from '@cast/types';

import { MetricsBox, MetricsBoxProps } from 'components/boxes';
import { WorkloadRecommendation } from 'features/workload-optimization';

import {
  LowConfidenceLastRecommendationMetric,
  LowConfidenceLookBackMetric,
} from '../LowConfidence/LowConfidenceMetrics';
import { LowConfidenceTooltip } from '../LowConfidence/LowConfidenceTooltip';
import { OptimizationNotEnabledTooltip } from '../OptimizationNotEnabledTooltip';
import { NoChangeReason } from './NoChangeReason';

const ArrowIcon = styled(Icons.ArrowRight)({
  marginLeft: 7,
  marginRight: 7,
  fontSize: 16,
});

const WarningCircle = styled(Icons.WarningCircle)({
  color: tokens.theme.data.yellow500,
  fontSize: 16,
});

type Props = {
  currentEl: ReactNode;
  recommendationEl: ReactNode;
  showArrow?: boolean;
  reverse?: boolean;
};

const ChartMetricsBox = ({
  titleProps,
  primaryValueProps,
  secondaryValueProps,
  sx,
  ...rest
}: MetricsBoxProps) => (
  <MetricsBox
    titleProps={{ variant: 'L10R', ...titleProps }}
    primaryValueProps={{ variant: 'h6', ...primaryValueProps }}
    secondaryValueProps={{
      variant: 'L10R',
      color: 'textSecondary',
      textTransform: 'none',
      ...secondaryValueProps,
    }}
    gap={4}
    sx={mergeSx({ zIndex: 0 }, sx)}
    {...rest}
  />
);

type CurrentRequestMetricBoxProps = {
  title: string;
  acronym: string;
  markerColor: string;
  from?: number;
  to?: number;
  isDisabled: boolean;
  onClick: () => void;
};

type RecommendedMetricBoxProps = {
  type: ComputeResourceType;
  title: string;
  acronym: string;
  markerColor: string;
  recommendation: WorkloadRecommendation | undefined;
  from?: number;
  to?: number;
  isReadOnly?: boolean;
  resourceConfig: WorkloadResourceConfig | undefined;
  isDisabled: boolean;
  onClick: () => void;
};

export const CurrentRequestMetricBox = ({
  title,
  acronym,
  markerColor,
  from,
  to,
  isDisabled,
  onClick,
}: CurrentRequestMetricBoxProps) => {
  const roundedFrom = from ? round(from, 3) : 'N/A';
  const roundedTo = to ? round(to, 3) : '--.--';
  const isEqual = roundedFrom === roundedTo;
  const fromColor = !from ? tokens.theme.data.grey300 : undefined;

  return (
    <ChartMetricsBox
      title={title}
      primaryValue={roundedFrom}
      primaryValueProps={{
        color: isEqual ? tokens.theme.data.green500 : fromColor,
        ...{ 'data-testid': 'current' },
      }}
      secondaryValue={acronym}
      markerColor={markerColor}
      onClick={onClick}
      disabled={isDisabled}
      data-testid="current"
    />
  );
};

export const RecommendedMetricBox = ({
  type,
  title,
  acronym,
  markerColor,
  recommendation,
  from,
  to,
  isReadOnly,
  resourceConfig,
  isDisabled,
  onClick,
}: RecommendedMetricBoxProps) => {
  const roundedFrom = from ? round(from, 3) : 'N/A';
  const roundedTo = to ? round(to, 3) : '--.--';
  const isEqual = roundedFrom === roundedTo;

  return (
    <OptimizationNotEnabledTooltip unit={type} wrapIf={isReadOnly}>
      <div>
        <ChartMetricsBox
          title={title}
          primaryValue={
            <>
              {' '}
              {roundedTo || '-.--'}
              {recommendation?.lowConfidence ? (
                <LowConfidenceTooltip
                  recommendation={recommendation}
                  tooltipPlacement="top"
                  content={
                    <>
                      <LowConfidenceLookBackMetric
                        resourceConfig={resourceConfig}
                        type={type}
                      />
                      <LowConfidenceLastRecommendationMetric
                        recommendation={recommendation}
                        type={type}
                      />
                    </>
                  }
                >
                  <WarningCircle weight="fill" data-testid="confidence-icon" />
                </LowConfidenceTooltip>
              ) : (
                !isReadOnly && (
                  <NoChangeReason
                    requested={from}
                    recommended={to}
                    config={resourceConfig}
                  />
                )
              )}
            </>
          }
          primaryValueProps={{
            color: isEqual
              ? isReadOnly
                ? tokens.theme.data.green300
                : tokens.theme.data.green500
              : isReadOnly
              ? tokens.theme.data.grey300
              : undefined,
            sx: isReadOnly
              ? {
                  textDecoration: 'underline dashed',
                  textUnderlinePosition: 'under',
                  textDecorationColor: tokens.theme.fg.structural.secondary,
                }
              : undefined,
            ...{ 'data-testid': 'recommended' },
          }}
          secondaryValue={acronym}
          secondaryValueProps={{
            color: isReadOnly
              ? tokens.theme.data.grey300
              : tokens.theme.fg.structural.secondary,
          }}
          markerColor={markerColor}
          onClick={onClick}
          disabled={isDisabled}
          data-testid="recommended"
        />
      </div>
    </OptimizationNotEnabledTooltip>
  );
};

export const RecommendedRequestMetricBoxes = ({
  currentEl,
  recommendationEl,
  showArrow,
  reverse,
}: Props) => {
  return (
    <Stack gap={2} data-testid="recommended-requests">
      <Stack
        direction={reverse ? 'row-reverse' : 'row'}
        alignItems="flex-end"
        columnGap={16}
      >
        {currentEl}

        {showArrow && <ArrowIcon weight="bold" />}

        {recommendationEl}
      </Stack>
    </Stack>
  );
};
